/**
 * PortalOnboarding - Onboarding checklist with progress tracking
 * Deployed to: frontend/src/pages/portal/PortalOnboarding.js
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Circle, Lock, Upload, FileSignature,
  ClipboardList, CreditCard, User, SkipForward
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const stepIcons = {
  review_info: User,
  upload_id: Upload,
  sign_engagement: FileSignature,
  complete_questionnaire: ClipboardList,
  make_payment: CreditCard,
};

const stepActions = {
  review_info: '/portal/profile-setup',
  upload_id: '/portal/documents',
  sign_engagement: '/portal/documents',
  complete_questionnaire: '/portal/questionnaires',
  make_payment: '/portal/invoices',
};

const PortalOnboarding = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [onboarding, setOnboarding] = useState(null);
  const [allOnboarding, setAllOnboarding] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadOnboarding(); }, [caseId]);

  const loadOnboarding = async () => {
    setLoading(true);
    try {
      if (caseId) {
        const data = await clientPortalService.getOnboardingForCase(caseId);
        setOnboarding(data.onboarding);
      } else {
        const data = await clientPortalService.getOnboarding();
        setAllOnboarding(data.onboarding || []);
        if (data.onboarding?.length === 1) {
          setOnboarding(data.onboarding[0]);
        }
      }
    } catch (err) {
      console.error('Failed to load onboarding:', err);
    } finally {
      setLoading(false);
    }
  };

  const completeStep = async (stepId) => {
    try {
      await clientPortalService.completeOnboardingStep(onboarding.case_id, stepId);
      loadOnboarding();
    } catch (err) {
      console.error('Failed to complete step:', err);
    }
  };

  const skipStep = async (stepId) => {
    try {
      await clientPortalService.skipOnboardingStep(onboarding.case_id, stepId);
      loadOnboarding();
    } catch (err) {
      alert(err.response?.data?.detail || 'Cannot skip this step');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
      </div>
    );
  }

  // Multi-case selection
  if (!onboarding && allOnboarding.length > 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate('/portal/dashboard')} className="text-virgo-teal-400 hover:text-white flex items-center gap-2 mb-6">
            <ArrowLeft size={20} /> Dashboard
          </button>
          <h1 className="text-2xl font-bold text-white mb-6">Getting Started</h1>
          <div className="space-y-4">
            {allOnboarding.map(ob => (
              <button key={ob.progress_id} onClick={() => setOnboarding(ob)}
                className="w-full bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-5 text-left hover:border-cyan-500/50 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">{ob.case_number || ob.case_description || 'Case'}</p>
                    <p className="text-virgo-teal-500 text-sm">{ob.template_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-cyan-400 font-bold">{ob.progress_percent}%</p>
                    <p className="text-virgo-teal-500 text-xs">{ob.completed_steps}/{ob.total_steps} steps</p>
                  </div>
                </div>
                <div className="w-full bg-virgo-navy-600 rounded-full h-2 mt-3">
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full transition-all" style={{ width: `${ob.progress_percent}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!onboarding) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="text-center text-virgo-teal-500">
          <CheckCircle className="mx-auto mb-3" size={48} />
          <p>No active onboarding</p>
          <button onClick={() => navigate('/portal/dashboard')} className="mt-3 text-cyan-400 hover:text-cyan-300">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => onboarding && allOnboarding.length > 1 ? setOnboarding(null) : navigate('/portal/dashboard')}
          className="text-virgo-teal-400 hover:text-white flex items-center gap-2 mb-6">
          <ArrowLeft size={20} /> {allOnboarding.length > 1 ? 'All Cases' : 'Dashboard'}
        </button>

        {/* Header */}
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 mb-6">
          <h1 className="text-2xl font-bold text-white mb-1">Getting Started</h1>
          <p className="text-virgo-teal-400">{onboarding.template_name} {onboarding.case_number ? `- ${onboarding.case_number}` : ''}</p>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-virgo-teal-400 text-sm">Progress</span>
              <span className="text-white font-bold">{onboarding.progress_percent}%</span>
            </div>
            <div className="w-full bg-virgo-navy-600 rounded-full h-3">
              <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${onboarding.progress_percent}%` }} />
            </div>
            <p className="text-virgo-teal-500 text-xs mt-1">{onboarding.completed_steps} of {onboarding.total_steps} steps completed</p>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {(onboarding.steps || []).map((step, i) => {
            const Icon = stepIcons[step.step_type] || Circle;
            const isCompleted = step.status === 'completed' || step.status === 'skipped';
            const actionUrl = stepActions[step.step_type];

            return (
              <div key={step.id}
                className={`bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border p-5 transition-all ${
                  isCompleted ? 'border-green-500/30 opacity-75' : 'border-virgo-teal-700/30'
                }`}>
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${
                    isCompleted ? 'bg-green-500/20' : 'bg-cyan-500/20'
                  }`}>
                    {isCompleted ? <CheckCircle className="text-green-400" size={20} /> : <Icon className="text-cyan-400" size={20} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className={`font-semibold ${isCompleted ? 'text-green-300 line-through' : 'text-white'}`}>
                        {step.title}
                      </h3>
                      {!step.is_required && !isCompleted && (
                        <span className="text-xs text-virgo-teal-500 bg-virgo-navy-600 px-2 py-0.5 rounded">Optional</span>
                      )}
                    </div>
                    {step.description && <p className="text-virgo-teal-500 text-sm mt-1">{step.description}</p>}
                    {step.status === 'skipped' && <p className="text-amber-400 text-xs mt-1">Skipped</p>}

                    {!isCompleted && (
                      <div className="flex gap-2 mt-3">
                        {actionUrl && (
                          <button onClick={() => navigate(actionUrl)}
                            className="px-4 py-1.5 bg-cyan-500/20 text-cyan-300 rounded-lg text-sm hover:bg-cyan-500/30 transition-all">
                            Start
                          </button>
                        )}
                        <button onClick={() => completeStep(step.id)}
                          className="px-4 py-1.5 bg-green-500/20 text-green-300 rounded-lg text-sm hover:bg-green-500/30 transition-all">
                          Mark Complete
                        </button>
                        {!step.is_required && (
                          <button onClick={() => skipStep(step.id)}
                            className="px-3 py-1.5 text-virgo-teal-500 hover:text-virgo-teal-300 text-sm flex items-center gap-1">
                            <SkipForward size={14} /> Skip
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {onboarding.status === 'completed' && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mt-6 text-center">
            <CheckCircle className="text-green-400 mx-auto mb-2" size={36} />
            <h3 className="text-green-300 font-bold text-lg">Onboarding Complete!</h3>
            <p className="text-virgo-teal-400 text-sm mt-1">You're all set. Thank you for completing your setup.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalOnboarding;
